import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { BoostingCampaign } from "@vviinn/user-managment-client-ts";
import { BoostingCampaignScoreContributionStrategyEnum } from "@vviinn/user-managment-client-ts/dist/models/BoostingCampaign";
import { useAppSelector } from "../../../store";
import { CompanyAndSiteIds } from "../../types";
import { useRequestHandlers } from "../../../app/hooks/useRequestHandlers";
import { DetailedWrapper } from "../../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { SlideOverFieldNamedRow } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { SlideOverWrapper } from "../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { NamedRow } from "../../../components/GeneralUI/NamedRow/NamedRow";
import { NamedNumberic } from "../../../ui/layout/molecules/NamedNumberic";
import { valueOrNA } from "../../../helpers";
import { primitiveSettingsTitles } from "../../../campaign/constants";
import { Fields } from "../../../ui/layout/detailedPages/Fields";
import { handleBoosting } from "../../../campaign/boosting/handleBoosting";
import { STRATEGY_LABEL, STRATEGY_OPTIONS } from "../../../campaign/boosting/constants";
import {
  PreferredValueDraft,
  inferColumnType,
  serializePreferredValues,
  rawValueToNumber,
  patchFirstPreferredDraft,
} from "../../../campaign/boosting/utils";
import { LinkedServiceCampaignsSection } from "../../../campaign/boosting/components/LinkedServiceCampaignsSection";
import { WeightNumberInput } from "../../../campaign/boosting/components/WeightNumberInput";
import { PreferredValuesFields } from "../../../campaign/boosting/components/PreferredValuesFields";

export const BoostingDetails: FC = () => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();
  const { boostingId } = useParams();
  const boosting = useAppSelector((state) =>
    state.boosting.boosting.find(({ id }) => id === boostingId),
  );
  const campaigns = useAppSelector((state) => state.campaigns.campaigns);
  const linkedServiceCampaigns = useMemo(
    () =>
      campaigns.filter((c) =>
        c.boosting?.linkedBoostingCampaigns?.some(
          (lbc) => lbc.campaign?.id === boostingId,
        ),
      ),
    [campaigns, boostingId],
  );
  const customFields = useAppSelector((state) => state.site.customFields);
  const customFieldsTitles = useAppSelector(
    (state) => state.site.customFieldsTitles,
  );
  const filters = useAppSelector((state) => state.campaigns.filters);
  const columnOptions = useMemo(
    () => [...filters, ...customFieldsTitles],
    [filters, customFieldsTitles],
  );

  const backLink = `/company/${companyId}/site/${siteId}/campaigns`;
  const navigate = useNavigate();
  const handlerParams = useRequestHandlers();
  const { handleUpsertBoosting, handleDeleteBoosting } = handleBoosting(
    handlerParams,
    { companyId, siteId },
  );

  useEffect(() => {
    if (!boosting) navigate(backLink);
  }, [boosting, siteId, backLink]);

  const upsertBoosting = async (newBoostingData: Partial<BoostingCampaign>) => {
    if (!boosting) return false;
    return !!(await handleUpsertBoosting({ ...boosting, ...newBoostingData }));
  };

  const [pvOpen, setPvOpen] = useState(false);
  const [drafts, setPreferredValueDrafts] = useState<PreferredValueDraft[]>([]);

  const colType = inferColumnType(boosting?.column, customFields);

  const openPvSlideOver = () => {
    const first = boosting?.preferredValues?.[0];
    setPreferredValueDrafts(
      first
        ? [{ weight: first.weight, rawValue: first.value }]
        : [{ weight: 1, rawValue: "" }],
    );
    setPvOpen(true);
  };

  const savePv = async () => {
    const preferredValues = serializePreferredValues(drafts);
    return upsertBoosting({
      preferredValues: preferredValues.length ? preferredValues : undefined,
    });
  };

  const pvChanged =
    drafts[0]?.rawValue !== (boosting?.preferredValues?.[0]?.value ?? "") ||
    drafts[0]?.weight !== (boosting?.preferredValues?.[0]?.weight ?? 1);

  return (
    <DetailedWrapper
      detailedPageType="detail"
      detailedPage="boosting"
      backLink={backLink}
      deleteAction={() => handleDeleteBoosting(boostingId)}
    >
      <Fields title="Boosting settings">
        <SlideOverFieldNamedRow
          onAccept={(value) => upsertBoosting({ name: value })}
          namedRowTitle={primitiveSettingsTitles.name}
          permissionsGroup="campaign:basic_config"
          slideOverValue={boosting?.name}
        />
        <SlideOverFieldNamedRow
          onAccept={(value) => upsertBoosting({ column: value })}
          namedRowTitle="Column"
          permissionsGroup="campaign:basic_config"
          slideOverValue={boosting?.column}
          options={columnOptions}
          slideOverInfo="Boosted values must be edited separately"
        />
        <SlideOverFieldNamedRow
          onAccept={(value) =>
            upsertBoosting({
              scoreContributionStrategy:
                value as BoostingCampaignScoreContributionStrategyEnum,
            })
          }
          namedRowTitle="Boosting strategy"
          permissionsGroup="campaign:basic_config"
          slideOverValue={boosting?.scoreContributionStrategy}
          customContent={
            boosting?.scoreContributionStrategy
              ? STRATEGY_LABEL[boosting.scoreContributionStrategy]
              : undefined
          }
          options={STRATEGY_OPTIONS}
        />

        {colType === "number" ? (
          <NamedRow
            title="Preferred value"
            content={valueOrNA(boosting?.preferredValues?.[0]?.value)}
            editPermissions="campaign:basic_config:update"
            onClick={openPvSlideOver}
          />
        ) : (
          <PreferredValuesFields
            boosting={boosting}
            upsertBoosting={upsertBoosting}
          />
        )}
      </Fields>

      {colType === "number" && (
        <SlideOverWrapper
          open={pvOpen}
          onClose={() => setPvOpen(false)}
          title="Preferred value"
          buttonProps={{
            disabled: !pvChanged,
            onClick: savePv,
          }}
        >
          <WeightNumberInput
            value={drafts[0]?.weight ?? 1}
            onChange={(v) =>
              setPreferredValueDrafts(
                patchFirstPreferredDraft(drafts[0], { weight: v }),
              )
            }
          />
          <NamedNumberic
            title="Value"
            value={rawValueToNumber(drafts[0]?.rawValue)}
            step="any"
            onChange={(target) =>
              setPreferredValueDrafts(
                patchFirstPreferredDraft(drafts[0], { rawValue: target.value }),
              )
            }
          />
        </SlideOverWrapper>
      )}

      <LinkedServiceCampaignsSection
        linkedServiceCampaigns={linkedServiceCampaigns}
        boosting={boosting}
        companyId={companyId}
        siteId={siteId}
      />
    </DetailedWrapper>
  );
};
